import FractionDigits from '@/Enums/Localization/FractionDigits.enum';
import MapScale from '@/Enums/Map/MapScale.enum';
import { BeeswaxReportProperties } from '@/Enums/Report/BeeswaxReportProperties.enum';
import NeuronReportView from '@/Enums/ReportingDashboard/ReportView/NeuronReportView.enum';
import ReportView from '@/Enums/ReportingDashboard/ReportView/ReportView.enum';
import ViewType from '@/Enums/ReportingDashboard/ViewType.enum';
import { LogoCell } from '@/components/Grid/LogoCell';
import { OMNICHANNEL_FIELD_MAPPING, OMNICHANNEL_REPORT_FIELDS } from '@/constants/beeswax';
import { GOOGLE_REPORT_FIELDS, IMPRESSION_TYPES, PUBLISHER_NAME } from '@/constants/reporting';
import { getTotalImpressions } from './reportingDashboard';
import { numberToPercent, numberToUsFormat, numberToUsd } from './valueFormatter/numberFormatter.helpers';

export const getOmnichannelReportingGridData = ({
  data,
  reportType,
  showCpm,
  showClientSpend,
  showTotalSpend,
  viewType,
  exceptionField,
  hideCtrAndClicks,
  showLineItemAlternativeId,
}) => {
  // Spend only reaches the campaign and placement reports, but the two toggles are independent:
  // an org can show CPM without showing Client Spend.
  const spendReportType = SPEND_REPORT_TYPES.includes(reportType);
  const includeClientSpend = !!showClientSpend && spendReportType;
  const includeTotalSpend = !!showTotalSpend && spendReportType;

  const result = {
    columns: getOmnichannelReportingGridColumns({
      viewType,
      exceptionField,
      hideCtrAndClicks,
      showCpm,
      includeClientSpend,
      includeTotalSpend,
      showLogo: reportType === IMPRESSION_TYPES.PUBLISHER,
      showLineItemAlternativeId: reportType === IMPRESSION_TYPES.PLACEMENT && showLineItemAlternativeId,
    }),
    data: [],
  };

  let currentData = data[OMNICHANNEL_REPORT_FIELDS[reportType]] ?? [];
  let type = OMNICHANNEL_FIELD_MAPPING[reportType] ?? reportType;

  const totalImpressions = getTotalImpressions(currentData);
  const mappedReportData = currentData?.map((item, i) => {
    const row = {
      id: `${item[type]}_${i}`,
      logo: item[BeeswaxReportProperties.LOGO],
      name: item[type],
      impressions: item[BeeswaxReportProperties.IMPRESSIONS],
      distribution: item[BeeswaxReportProperties.IMPRESSIONS] / totalImpressions,
      frequency: item[BeeswaxReportProperties.FREQUENCY],
      reach: item[BeeswaxReportProperties.REACH],
      vcr: item[BeeswaxReportProperties.VCR],
      ctr: item[BeeswaxReportProperties.CTR],
      ltr: item[BeeswaxReportProperties.LTR],
      clicks: item[BeeswaxReportProperties.CLICKS],
      conversions: item[BeeswaxReportProperties.IP_CONVERSIONS],
      conversion: item[BeeswaxReportProperties.IP_CONVERSIONS] / item[BeeswaxReportProperties.IMPRESSIONS],
    };
    if (showCpm) {
      row.cpm = item[BeeswaxReportProperties.CPM_USD];
      row.spend = item[BeeswaxReportProperties.SPEND];
    }
    if (showLineItemAlternativeId) {
      row.lineItemAlternativeId = item[BeeswaxReportProperties.LINE_ITEM_ALTERNATIVE_ID];
    }
    const conversions = item[BeeswaxReportProperties.IP_CONVERSIONS];
    if (includeClientSpend) {
      row.clientSpend = item[BeeswaxReportProperties.CLIENT_SPEND];
      row.clientSpendCpa = getCpa(row.clientSpend, conversions);
    }
    if (includeTotalSpend) {
      row.totalSpendCpa = getCpa(item[BeeswaxReportProperties.TOTAL_SPEND], conversions);
    }
    return row;
  });

  result.data = mappedReportData;

  return result;
};

const NAME_COLUMN = {
  field: 'name',
  headerName: 'Name',
  flex: 0.75,
};
const IMPRESSIONS_COLUMN = {
  field: 'impressions',
  headerName: 'Impressions',
  valueFormatter: value => numberToUsFormat(value, { maximumFractionDigits: 0 }),
  flex: 0.25,
};
const DISTRIBUTION_COLUMN = {
  field: 'distribution',
  headerName: 'Distribution',
  valueFormatter: value => numberToPercent(value),
  flex: 0.25,
};
const REACH_COLUMN = {
  field: 'reach',
  headerName: 'Reach',
  valueFormatter: value => numberToUsFormat(value),
  flex: 0.25,
};
const FREQUENCY_COLUMN = {
  field: 'frequency',
  headerName: 'Frequency',
  valueFormatter: value => `${numberToUsFormat(value)}${value > 30 ? '*' : ''}`,
  flex: 0.25,
};
const VCR_COLUMN = {
  field: 'vcr',
  headerName: 'VCR',
  valueGetter: value => numberToPercent(value),
  flex: 0.25,
};
const CTR_COLUMN = {
  field: 'ctr',
  headerName: 'CTR',
  valueGetter: value => numberToPercent(value, { maximumFractionDigits: FractionDigits.Ctr }),
  flex: 0.25,
};
const LTR_COLUMN = {
  field: 'ltr',
  headerName: 'LTR',
  valueGetter: value => numberToPercent(value, { maximumFractionDigits: FractionDigits.Ltr }),
  flex: 0.25,
};
const LOGO_COLUMN = {
  field: 'logo',
  headerName: '',
  maxWidth: 80,
  display: 'flex',
  renderCell: LogoCell,
  sortable: false,
  disableExport: true,
};
const ALT_ID_COLUMN = {
  field: 'lineItemAlternativeId',
  headerName: 'Line Item Alternative ID',
  flex: 1,
};
const CLIENT_SPEND_COLUMN = {
  field: 'clientSpend',
  headerName: BeeswaxReportProperties.CLIENT_SPEND,
  valueFormatter: value => numberToUsd(value),
  flex: 0.25,
};
const CLIENT_SPEND_CPA_COLUMN = {
  field: 'clientSpendCpa',
  headerName: BeeswaxReportProperties.CLIENT_SPEND_CPA,
  valueFormatter: value => cpaFormatter(value),
  flex: 0.3,
};
const TOTAL_SPEND_CPA_COLUMN = {
  field: 'totalSpendCpa',
  headerName: BeeswaxReportProperties.TOTAL_SPEND_CPA,
  valueFormatter: value => cpaFormatter(value),
  flex: 0.3,
};

const SPEND_REPORT_TYPES = [IMPRESSION_TYPES.CAMPAIGN, IMPRESSION_TYPES.PLACEMENT];

// Both sides can arrive as strings, and a missing spend is null rather than zero. Unguarded, a
// string "0" divides to Infinity and a null spend to 0 — numberToUsd renders both as "$0.00"
// instead of "N/A". A real zero spend stays valid.
const getCpa = (spend, conversions) => {
  const spendValue = spend == null ? Number.NaN : Number(spend);
  const conversionsValue = Number(conversions);
  if (!Number.isFinite(spendValue) || !Number.isFinite(conversionsValue) || conversionsValue <= 0) {
    return null;
  }
  return spendValue / conversionsValue;
};
const cpaFormatter = value => (value == null ? 'N/A' : numberToUsd(value));

const clicksColumn = (hideClicks = false) => ({
  field: 'clicks',
  headerName: 'Clicks',
  valueGetter: value => numberToUsFormat(hideClicks ? 0 : value),
  flex: 0.25,
});

const getLogoColumn = options => (options.showLogo ? [LOGO_COLUMN] : []);
const getAltIdColumn = options => (options.showLineItemAlternativeId ? [ALT_ID_COLUMN] : []);
const COMMON_OMNICHANNEL_COLUMNS = [IMPRESSIONS_COLUMN, DISTRIBUTION_COLUMN, REACH_COLUMN, FREQUENCY_COLUMN];

const OMNICHANNEL_COLUMNS_BY_VIEW_TYPE = {
  [ViewType.Delivery]: ({ showCpm, exceptionField, hideCtrAndClicks }) => {
    const columns = [
      {
        ...VCR_COLUMN,
        valueGetter: value => numberToPercent(exceptionField === 'Display' ? 0 : value),
      },
      {
        ...CTR_COLUMN,
        valueGetter: value =>
          numberToPercent(hideCtrAndClicks ? 0 : value, { maximumFractionDigits: FractionDigits.Ctr }),
      },
      LTR_COLUMN,
    ];
    if (showCpm) {
      columns.push(
        { field: 'cpm', headerName: 'CPM', valueFormatter: value => numberToUsd(value), flex: 0.25 },
        {
          field: 'spend',
          headerName: 'Spend',
          valueFormatter: value => numberToUsd(value),
          flex: 0.25,
        },
      );
    }
    return columns;
  },
  [ViewType.Attribution]: ({ hideCtrAndClicks, includeClientSpend, includeTotalSpend }) => {
    const columns = [
      clicksColumn(hideCtrAndClicks),
      {
        field: 'conversions',
        headerName: 'Conversions',
        valueFormatter: value => numberToUsFormat(value),
        flex: 0.25,
      },
      {
        field: 'conversion',
        headerName: 'Conversion %',
        valueFormatter: value => {
          if (value === 0) return 'N/A';
          if (value < 0.01) return numberToPercent(value, { maximumFractionDigits: FractionDigits.Ctr });
          return numberToPercent(value, { maximumFractionDigits: FractionDigits.Extended });
        },
        flex: 0.35,
      },
    ];
    if (includeClientSpend) {
      columns.push(CLIENT_SPEND_COLUMN, CLIENT_SPEND_CPA_COLUMN);
    }
    if (includeTotalSpend) {
      columns.push(TOTAL_SPEND_CPA_COLUMN);
    }
    return columns;
  },
};

export const getOmnichannelReportingGridColumns = options => [
  ...getLogoColumn(options),
  NAME_COLUMN,
  ...getAltIdColumn(options),
  ...COMMON_OMNICHANNEL_COLUMNS,
  ...OMNICHANNEL_COLUMNS_BY_VIEW_TYPE[options.viewType](options),
];

const DOOH_COLUMNS = [NAME_COLUMN, IMPRESSIONS_COLUMN, DISTRIBUTION_COLUMN];
const DOOH_PUBLISHERS_COLUMNS = [LOGO_COLUMN, ...DOOH_COLUMNS];

export const getDoohReportingGridData = ({ data, reportType }) => {
  const columns = reportType === ReportView.Publisher ? DOOH_PUBLISHERS_COLUMNS : DOOH_COLUMNS;

  const configMap = {
    [ReportView.Date]: {
      type: BeeswaxReportProperties.DAY,
      data: data?.dateData?.slice(0, -1),
    },
    [ReportView.Campaign]: { type: BeeswaxReportProperties.CAMPAIGN_NAME, data: data?.campaignNameData },
    [ReportView.Placement]: { type: BeeswaxReportProperties.LINE_ITEM_NAME, data: data?.placementData },
    [ReportView.Creative]: { type: BeeswaxReportProperties.CREATIVE_NAME, data: data?.creativeData },
    [NeuronReportView.VenueType]: { type: NeuronReportView.VenueType, data: data?.venueTypeData },
    [ReportView.Advertiser]: { type: BeeswaxReportProperties.ADVERTISER, data: data?.advertiserData },
    [ReportView.Publisher]: { type: BeeswaxReportProperties.PUBLISHER_NAME, data: data?.publisherNameData },
    [ReportView.DaysOfWeek]: { type: BeeswaxReportProperties.DAY_OF_WEEK, data: data?.dayOfWeekData },
    [MapScale.State]: { type: BeeswaxReportProperties.GEO_STATE, data: data?.stateData },
    [MapScale.Dma]: { type: BeeswaxReportProperties.DMA_NAME, data: data?.dmaData },
    [MapScale.Zip]: { type: BeeswaxReportProperties.GEO_ZIP, data: data?.zipData },
    [MapScale.County]: { type: BeeswaxReportProperties.GEO_COUNTY, data: data?.countyData },
    [MapScale.City]: { type: BeeswaxReportProperties.CITY, data: data?.cityData },
  };

  const cfg = configMap[reportType] || { type: reportType, data: [] };
  const type = cfg.type;
  let currentData = cfg.data ?? [];

  const result = {
    columns,
    data: [],
  };

  const totalImpressions = getTotalImpressions(currentData);

  const mappedReportData = currentData?.map((item, i) => {
    const row = {
      id: `${item[type]}_${i}`,
      name: item[type],
      logo: item[BeeswaxReportProperties.LOGO],
      impressions: item[BeeswaxReportProperties.IMPRESSIONS],
      distribution: item[BeeswaxReportProperties.IMPRESSIONS] / totalImpressions,
    };

    return row;
  });

  result.data = mappedReportData;

  return result;
};

const COMMON_GOOGLE_COLUMNS = [NAME_COLUMN, IMPRESSIONS_COLUMN, DISTRIBUTION_COLUMN];
const getPlacementGoogleColumns = options => {
  return [
    NAME_COLUMN,
    ...getAltIdColumn(options),
    IMPRESSIONS_COLUMN,
    DISTRIBUTION_COLUMN,
    REACH_COLUMN,
    FREQUENCY_COLUMN,
    VCR_COLUMN,
    clicksColumn(),
    CTR_COLUMN,
  ].map(item => ({
    ...item,
    flex: item?.flex ?? 0.5,
  }));
};

const getGoogleGridReportingColumns = ({ reportType, showLineItemAlternativeId }) => {
  switch (reportType) {
    case ReportView.Date:
    case ReportView.Device:
    case ReportView.Campaign:
    case ReportView.Advertiser:
    case ReportView.App:
      return [...COMMON_GOOGLE_COLUMNS, REACH_COLUMN, FREQUENCY_COLUMN, VCR_COLUMN, clicksColumn(), CTR_COLUMN].map(
        item => ({
          ...item,
          flex: item?.flex ?? 0.5,
        }),
      );
    case ReportView.Placement:
      return getPlacementGoogleColumns({ showLineItemAlternativeId });
    case ReportView.Channel:
    case ReportView.DaysOfWeek:
    case ReportView.Daypart:
    case ReportView.Creative:
    case MapScale.Dma:
    case MapScale.State:
      return [...COMMON_GOOGLE_COLUMNS, VCR_COLUMN, clicksColumn(), CTR_COLUMN].map(item => ({
        ...item,
        flex: item?.flex ?? 1,
      }));
    case ReportView.Publisher:
      return [
        LOGO_COLUMN,
        ...COMMON_GOOGLE_COLUMNS.map(item => ({
          ...item,
        })),
      ];
    default:
      return [];
  }
};

const googleReachReportMapper = (reportType, totalImpressions) => (item, i) => {
  return {
    id: `${item[reportType]}_${i}`,
    name: item[reportType],
    impressions: item[BeeswaxReportProperties.IMPRESSIONS],
    frequency: item[BeeswaxReportProperties.FREQUENCY],
    reach: item[BeeswaxReportProperties.REACH],
    vcr: item[BeeswaxReportProperties.VCR],
    clicks: item[BeeswaxReportProperties.CLICKS],
    ctr: item[BeeswaxReportProperties.CTR],
    distribution: item[BeeswaxReportProperties.IMPRESSIONS] / totalImpressions,
  };
};

const googleStandardReportMapper = reportType => (item, i) => {
  return {
    id: `${item[reportType]}_${i}`,
    name: item[reportType],
    impressions: item[BeeswaxReportProperties.IMPRESSIONS],
    vcr: item[BeeswaxReportProperties.VCR],
    clicks: item[BeeswaxReportProperties.CLICKS],
    ctr: item[BeeswaxReportProperties.CTR],
    distribution: item[BeeswaxReportProperties.DISTRIBUTION],
  };
};

const googlePublisherMapper = () => (item, i) => {
  return {
    id: `${item[PUBLISHER_NAME]}_${i}`,
    name: item[BeeswaxReportProperties.PUBLISHER_NAME],
    logo: item[BeeswaxReportProperties.LOGO],
    impressions: item[BeeswaxReportProperties.IMPRESSIONS],
    distribution: item[BeeswaxReportProperties.DISTRIBUTION],
  };
};

const googlePlacementReportMapper = totalImpressions => (item, i) => {
  return {
    id: `${item[BeeswaxReportProperties.LINE_ITEM_NAME]}_${i}`,
    lineItemAlternativeId: item[BeeswaxReportProperties.LINE_ITEM_ALTERNATIVE_ID],
    name: item[BeeswaxReportProperties.LINE_ITEM_NAME],
    impressions: item[BeeswaxReportProperties.IMPRESSIONS],
    frequency: item[BeeswaxReportProperties.FREQUENCY],
    reach: item[BeeswaxReportProperties.REACH],
    vcr: item[BeeswaxReportProperties.VCR],
    clicks: item[BeeswaxReportProperties.CLICKS],
    ctr: item[BeeswaxReportProperties.CTR],
    distribution: item[BeeswaxReportProperties.IMPRESSIONS] / totalImpressions,
  };
};

const REACH_REPORTS = new Set([
  ReportView.Date,
  ReportView.Device,
  ReportView.Campaign,
  ReportView.Advertiser,
  ReportView.Placement,
  ReportView.App,
]);

export const getGoogleReportingGridData = ({ data, reportType, ...options }) => {
  const result = {
    columns: getGoogleGridReportingColumns({
      reportType,
      ...options,
    }),
    data: [],
  };

  const totalImpressions = REACH_REPORTS.has(reportType)
    ? getTotalImpressions(data[GOOGLE_REPORT_FIELDS[reportType]])
    : 0;

  const configMap = {
    [ReportView.Date]: {
      data: data?.dateData,
      mapper: googleReachReportMapper(BeeswaxReportProperties.DAY, totalImpressions),
    },
    [ReportView.App]: {
      data: data?.appUrlReport,
      mapper: googleReachReportMapper(BeeswaxReportProperties.APP, totalImpressions),
    },
    [ReportView.Channel]: {
      data: data?.channelData,
      mapper: googleStandardReportMapper(BeeswaxReportProperties.DIGITAL_CHANNEL),
    },
    [ReportView.Device]: {
      data: data?.deviceData,
      mapper: googleReachReportMapper(BeeswaxReportProperties.DEVICE_TYPE, totalImpressions),
    },
    [ReportView.Campaign]: {
      data: data?.campaignData,
      mapper: googleReachReportMapper(BeeswaxReportProperties.CAMPAIGN_NAME, totalImpressions),
    },
    [ReportView.Advertiser]: {
      data: data?.advertiserData,
      mapper: googleReachReportMapper(BeeswaxReportProperties.ADVERTISER_NAME, totalImpressions),
    },
    [ReportView.Placement]: { data: data?.placementData, mapper: googlePlacementReportMapper(totalImpressions) },
    [ReportView.Creative]: {
      data: data?.creativeData,
      mapper: googleStandardReportMapper(BeeswaxReportProperties.CREATIVE_NAME),
    },

    [ReportView.Publisher]: { data: data?.publisherData, mapper: googlePublisherMapper() },
    [ReportView.DaysOfWeek]: {
      data: data?.daysOfWeekData,
      mapper: googleStandardReportMapper(BeeswaxReportProperties.DAY_OF_WEEK),
    },
    [ReportView.Daypart]: {
      data: data?.daypartData,
      mapper: googleStandardReportMapper(BeeswaxReportProperties.DAYPART),
    },
    [MapScale.State]: {
      data: data?.stateData,
      mapper: googleStandardReportMapper(BeeswaxReportProperties.GEO_STATE),
    },
    [MapScale.Dma]: { data: data?.dmaData, mapper: googleStandardReportMapper(BeeswaxReportProperties.DMA_NAME) },
  };

  const cfg = configMap[reportType];
  if (cfg) {
    result.data = cfg.data ? cfg.data.map(cfg.mapper) : [];
  }

  return result;
};
